import React, { createContext, useContext, useState, useEffect } from 'react';
import { Asset, Campaign, NarrativeSeason, ChipDNA, AssetType, AssetStatus, UserProfile, ScenarioOption, EnvironmentOption, AttireOption, AppNotification, NotificationType } from '../types.ts';
import { DEFAULT_DNA, SCENARIOS, ENVIRONMENTS, ATTIRE_OPTIONS } from '../constants.ts';
import { db, storage, isConfigured } from '../lib/firebase.ts';
import { 
  collection, 
  addDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy, 
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  writeBatch,
  deleteField,
  limit
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { useAuth } from './AuthContext.tsx';

interface StoreContextType {
  assets: Asset[];
  campaigns: Campaign[];
  narrativeSeasons: NarrativeSeason[];
  dna: ChipDNA;
  userProfile: UserProfile | null;
  notifications: AppNotification[];
  
  // Dynamic Options
  scenarios: ScenarioOption[];
  environments: EnvironmentOption[];
  attire: AttireOption[];
  
  addAsset: (asset: Omit<Asset, 'createdBy'> & { createdBy?: string }) => Promise<void>;
  updateAsset: (id: string, data: Partial<Asset>) => Promise<void>;
  updateDNA: (dna: ChipDNA) => Promise<void>;
  deleteAsset: (id: string) => Promise<void>;
  deleteAssets: (ids: string[]) => Promise<void>;
  addCampaign: (campaign: Campaign) => Promise<void>;
  updateCampaign: (id: string, data: Partial<Campaign>) => Promise<void>;
  deleteCampaign: (id: string) => Promise<void>;
  addNarrative: (narrative: NarrativeSeason) => Promise<void>;
  completeOnboarding: () => void;
  updateUserExtendedProfile: (data: Partial<UserProfile>) => Promise<void>;
  migrateLegacyImages: () => Promise<number>;
  
  // Notification Methods
  addNotification: (type: NotificationType, title: string, message: string, link?: string) => Promise<void>;
  markNotificationRead: (id: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  clearNotifications: () => Promise<void>;

  // Add Dynamic Options
  addScenario: (scenario: ScenarioOption) => Promise<void>;
  addEnvironment: (env: EnvironmentOption) => Promise<void>;
  addAttire: (attire: AttireOption) => Promise<void>;
  
  isLoading: boolean;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [narrativeSeasons, setNarrativeSeasons] = useState<NarrativeSeason[]>([]);
  const [dna, setDna] = useState<ChipDNA>(DEFAULT_DNA);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  
  // Dynamic Options State
  const [scenarios, setScenarios] = useState<ScenarioOption[]>([]);
  const [environments, setEnvironments] = useState<EnvironmentOption[]>([]);
  const [attire, setAttire] = useState<AttireOption[]>([]);
  
  const [isLoading, setIsLoading] = useState(true);

  // Helper to seed defaults if collections are empty
  const checkAndSeedDefaults = async () => {
    if (!db) return;
    
    try {
      // 1. Scenarios
      const scenarioRef = collection(db, 'options_scenarios');
      const sSnap = await getDocs(scenarioRef);
      if (sSnap.empty) {
        console.log('Seeding default scenarios...');
        const batch = writeBatch(db);
        SCENARIOS.forEach(item => {
          const docRef = doc(scenarioRef, item.id);
          batch.set(docRef, item);
        });
        await batch.commit();
      }

      // 2. Environments
      const envRef = collection(db, 'options_environments');
      const eSnap = await getDocs(envRef);
      if (eSnap.empty) {
        console.log('Seeding default environments...');
        const batch = writeBatch(db);
        ENVIRONMENTS.forEach(item => {
          const docRef = doc(envRef, item.id);
          batch.set(docRef, item);
        });
        await batch.commit();
      }

      // 3. Attire
      const attireRef = collection(db, 'options_attire');
      const aSnap = await getDocs(attireRef);
      if (aSnap.empty) {
        console.log('Seeding default attire...');
        const batch = writeBatch(db);
        ATTIRE_OPTIONS.forEach(item => {
          const docRef = doc(attireRef, item.id);
          batch.set(docRef, item);
        });
        await batch.commit();
      }
    } catch (e) {
      console.error("Error seeding defaults:", e);
    }
  };

  // Load Data from Firestore when User is authenticated
  useEffect(() => {
    if (!isConfigured || !user || !db) {
      setAssets([]);
      setCampaigns([]);
      setNarrativeSeasons([]);
      setUserProfile(null);
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Initial Seed Check
    checkAndSeedDefaults();

    // Listeners
    const qAssets = query(collection(db, 'assets'), orderBy('createdAt', 'desc'));
    const unsubAssets = onSnapshot(qAssets, (snapshot) => {
      const loadedAssets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Asset));
      setAssets(loadedAssets);
    });

    const qCampaigns = query(collection(db, 'campaigns'));
    const unsubCampaigns = onSnapshot(qCampaigns, (snapshot) => {
      const loadedCampaigns = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Campaign));
      setCampaigns(loadedCampaigns);
    });

    const qNarratives = query(collection(db, 'narratives'));
    const unsubNarratives = onSnapshot(qNarratives, (snapshot) => {
      const loadedNarratives = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as NarrativeSeason));
      setNarrativeSeasons(loadedNarratives);
    });

    // Options Listeners
    const unsubScenarios = onSnapshot(collection(db, 'options_scenarios'), (snap) => {
        const items = snap.docs.map(d => d.data() as ScenarioOption);
        // Sort: Default first, then custom
        setScenarios(items.length > 0 ? items : SCENARIOS);
    });

    const unsubEnvs = onSnapshot(collection(db, 'options_environments'), (snap) => {
        const items = snap.docs.map(d => d.data() as EnvironmentOption);
        setEnvironments(items.length > 0 ? items : ENVIRONMENTS);
    });

    const unsubAttire = onSnapshot(collection(db, 'options_attire'), (snap) => {
        const items = snap.docs.map(d => d.data() as AttireOption);
        setAttire(items.length > 0 ? items : ATTIRE_OPTIONS);
    });

    // Notifications Listener
    const qNotifications = query(
      collection(db, `users/${user.uid}/notifications`), 
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    const unsubNotifications = onSnapshot(qNotifications, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AppNotification));
      setNotifications(items);
    });

    const dnaRef = doc(db, 'settings', 'chip_dna');
    getDoc(dnaRef).then((docSnap) => {
      if (docSnap.exists()) {
        const savedData = docSnap.data() as Partial<ChipDNA>;
        const mergedDNA: ChipDNA = {
          ...DEFAULT_DNA,
          ...savedData,
          core: { ...DEFAULT_DNA.core, ...(savedData.core || {}) },
          visual: { ...DEFAULT_DNA.visual, ...(savedData.visual || {}) },
          voice: { ...DEFAULT_DNA.voice, ...(savedData.voice || {}) },
          personality: { ...DEFAULT_DNA.personality, ...(savedData.personality || {}) },
        };
        setDna(mergedDNA);
      } else {
        const cleanDefault = JSON.parse(JSON.stringify(DEFAULT_DNA));
        setDoc(dnaRef, cleanDefault);
        setDna(DEFAULT_DNA);
      }
    });

    const userRef = doc(db, 'users', user.uid);
    const unsubUser = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        setUserProfile(docSnap.data() as UserProfile);
      } else {
        setUserProfile({});
      }
    });

    setIsLoading(false);

    return () => {
      unsubAssets();
      unsubCampaigns();
      unsubNarratives();
      unsubUser();
      unsubScenarios();
      unsubEnvs();
      unsubAttire();
      unsubNotifications();
    };
  }, [user]);

  const addAsset = async (asset: Omit<Asset, 'createdBy'> & { createdBy?: string }) => {
    if (!user || !db || !storage) {
        alert("Cannot add asset: No active session or database connection.");
        return;
    }

    try {
      let finalUrl = asset.url;

      // Image Handling: Upload Base64 to Storage
      if (asset.type === AssetType.Image && asset.url.startsWith('data:')) {
        const storageRef = ref(storage, `assets/${user.uid}/${Date.now()}_${Math.random().toString(36).substring(7)}`);
        await uploadString(storageRef, asset.url, 'data_url');
        finalUrl = await getDownloadURL(storageRef);
      }
      
      const { id, ...assetData } = asset;
      
      // Calculate best display name: Auth Name -> Email Handle -> "Unknown"
      let creatorName = user.displayName;
      if (!creatorName && user.email) {
        creatorName = user.email.split('@')[0];
        // Capitalize first letter
        creatorName = creatorName.charAt(0).toUpperCase() + creatorName.slice(1);
      }
      
      // Augment with creator info if missing
      const enrichedData = {
        ...assetData,
        url: finalUrl,
        createdBy: asset.createdBy || user.uid,
        createdByName: asset.createdByName || creatorName || 'Unknown Creator',
        createdByAvatar: asset.createdByAvatar || user.photoURL || userProfile?.photoURL || null,
        status: asset.status || AssetStatus.Available
      };
      
      await addDoc(collection(db, 'assets'), enrichedData);
    } catch (error) {
      console.error("Error adding asset:", error);
      throw error;
    }
  };

  const updateAsset = async (id: string, data: Partial<Asset>) => {
    if (!user || !db) return;
    try {
      const assetRef = doc(db, 'assets', id);
      await updateDoc(assetRef, data);
    } catch (error) {
      console.error("Error updating asset:", error);
      throw error;
    }
  };

  const deleteAsset = async (id: string) => {
    if (!user || !db || !storage) {
        alert("Cannot delete: Database connection is missing.");
        return;
    }
    try {
      const docRef = doc(db, 'assets', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const assetData = docSnap.data() as Asset;
        if (assetData.url && assetData.url.includes('firebasestorage.googleapis.com')) {
          try {
              const fileRef = ref(storage, assetData.url);
              await deleteObject(fileRef);
          } catch (storageErr) {
              console.warn('Failed to delete storage file:', storageErr);
          }
        }
      }
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting asset:", error);
      throw error;
    }
  };

  const deleteAssets = async (ids: string[]) => {
    if (!user || !db || !storage) {
        alert("Cannot delete assets: Database connection is missing.");
        return;
    }
    try {
      const batch = writeBatch(db);
      for (const id of ids) {
         const docRef = doc(db, 'assets', id);
         const docSnap = await getDoc(docRef);
         if (docSnap.exists()) {
             const data = docSnap.data() as Asset;
             if (data.url && data.url.includes('firebasestorage.googleapis.com')) {
                 try {
                     const fileRef = ref(storage, data.url);
                     await deleteObject(fileRef);
                 } catch (e) {}
             }
             batch.delete(docRef);
         }
      }
      await batch.commit();
    } catch (error) {
      console.error("Error deleting batch assets:", error);
      throw error;
    }
  };

  const updateDNA = async (newDna: ChipDNA) => {
    if (!user || !db) return;
    try {
      const cleanData = JSON.parse(JSON.stringify(newDna));
      await setDoc(doc(db, 'settings', 'chip_dna'), cleanData);
      setDna(newDna);
    } catch (error) {
      console.error("Error updating DNA:", error);
      throw error;
    }
  };

  const addCampaign = async (campaign: Campaign) => {
    if (!user || !db) return;
    const { id, ...campData } = campaign;
    try {
      await addDoc(collection(db, 'campaigns'), campData);
    } catch (error) {
      console.error("Error adding campaign:", error);
    }
  };

  const updateCampaign = async (id: string, data: Partial<Campaign>) => {
    if (!user || !db) return;
    try {
      const campaignRef = doc(db, 'campaigns', id);
      await updateDoc(campaignRef, data);
    } catch (error) {
      console.error("Error updating campaign:", error);
      throw error;
    }
  };

  const deleteCampaign = async (id: string) => {
    if (!user || !db) {
        alert("Cannot delete campaign: Database connection missing.");
        return;
    }
    try {
      const batch = writeBatch(db);
      const assetsToUnassign = assets.filter(a => a.campaignId === id);
      assetsToUnassign.forEach(asset => {
         const assetRef = doc(db, 'assets', asset.id);
         batch.update(assetRef, { campaignId: deleteField() });
      });
      const campaignRef = doc(db, 'campaigns', id);
      batch.delete(campaignRef);
      await batch.commit();
    } catch (error) {
      console.error("Error deleting campaign:", error);
      throw error;
    }
  };

  const addNarrative = async (narrative: NarrativeSeason) => {
    if (!user || !db) return;
    const { id, ...data } = narrative;
    try {
      await addDoc(collection(db, 'narratives'), data);
    } catch (error) {
      console.error("Error adding narrative:", error);
      throw error;
    }
  };

  const completeOnboarding = async () => {
    if (!user || !db) return;
    const newData = { onboardingCompleted: true };
    await setDoc(doc(db, 'users', user.uid), newData, { merge: true });
    setUserProfile(prev => ({ ...prev, ...newData }));
  };

  const updateUserExtendedProfile = async (data: Partial<UserProfile>) => {
    if (!user || !db) return;
    try {
       await setDoc(doc(db, 'users', user.uid), data, { merge: true });
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  };

  const migrateLegacyImages = async (): Promise<number> => {
    if (!user || !db || !storage) return 0;
    
    let migratedCount = 0;
    try {
      const assetsSnapshot = await getDocs(collection(db, 'assets'));
      
      for (const docSnap of assetsSnapshot.docs) {
        const asset = docSnap.data() as Asset;
        const assetId = docSnap.id;
        let newUrl = '';
        let needsUpdate = false;

        try {
          if (asset.type === AssetType.Image && asset.url && asset.url.startsWith('data:')) {
            const storageRef = ref(storage, `assets/${user.uid}/migrated_${Date.now()}_${Math.random().toString(36).substring(7)}`);
            await uploadString(storageRef, asset.url, 'data_url');
            newUrl = await getDownloadURL(storageRef);
            needsUpdate = true;
          } 
          else if (asset.type === AssetType.Video && asset.url && asset.url.includes('googlevideo.com')) {
             try {
                const response = await fetch(asset.url);
                if (response.ok) {
                    const blob = await response.blob();
                    const storageRef = ref(storage, `assets/${user.uid}/migrated_video_${Date.now()}.mp4`);
                    await uploadBytes(storageRef, blob);
                    newUrl = await getDownloadURL(storageRef);
                    needsUpdate = true;
                }
             } catch (fetchErr) {}
          }

          if (needsUpdate && newUrl) {
            await updateDoc(doc(db, 'assets', assetId), { url: newUrl });
            migratedCount++;
          }
        } catch (itemErr) {}
      }

      if (dna.visual.likenessImage && dna.visual.likenessImage.startsWith('data:')) {
        const storageRef = ref(storage, `settings/chip_dna/reference_${Date.now()}.jpg`);
        await uploadString(storageRef, dna.visual.likenessImage, 'data_url');
        const newUrl = await getDownloadURL(storageRef);
        const newDna = { ...dna, visual: { ...dna.visual, likenessImage: newUrl } };
        await setDoc(doc(db, 'settings', 'chip_dna'), newDna);
        setDna(newDna);
        migratedCount++;
      }

    } catch (e) {
      console.error("Migration failed:", e);
      throw e;
    }

    return migratedCount;
  };

  const addScenario = async (scenario: ScenarioOption) => {
    if (!user || !db) return;
    try {
       await setDoc(doc(db, 'options_scenarios', scenario.id), scenario);
    } catch (e) {}
  };

  const addEnvironment = async (env: EnvironmentOption) => {
    if (!user || !db) return;
    try {
       await setDoc(doc(db, 'options_environments', env.id), env);
    } catch (e) {}
  };

  const addAttire = async (attireOption: AttireOption) => {
    if (!user || !db) return;
    try {
       await setDoc(doc(db, 'options_attire', attireOption.id), attireOption);
    } catch (e) {}
  };

  const addNotification = async (type: NotificationType, title: string, message: string, link?: string) => {
    if (!user || !db) return;
    try {
       await addDoc(collection(db, `users/${user.uid}/notifications`), {
         type,
         title,
         message,
         timestamp: Date.now(),
         read: false,
         link: link || null
       });
    } catch (e) {}
  };

  const markNotificationRead = async (id: string) => {
    if (!user || !db) return;
    try {
      const docRef = doc(db, `users/${user.uid}/notifications`, id);
      await updateDoc(docRef, { read: true });
    } catch (e) {}
  };

  const markAllNotificationsRead = async () => {
     if (!user || !db) return;
     try {
       const batch = writeBatch(db);
       notifications.filter(n => !n.read).forEach(n => {
          const docRef = doc(db, `users/${user.uid}/notifications`, n.id);
          batch.update(docRef, { read: true });
       });
       await batch.commit();
     } catch (e) {}
  };

  const clearNotifications = async () => {
    if (!user || !db) return;
    try {
      const batch = writeBatch(db);
      notifications.forEach(n => {
         const docRef = doc(db, `users/${user.uid}/notifications`, n.id);
         batch.delete(docRef);
      });
      await batch.commit();
    } catch (e) {}
  };

  return (
    <StoreContext.Provider value={{ 
        assets, campaigns, narrativeSeasons, dna, userProfile, notifications,
        scenarios, environments, attire,
        addAsset, updateAsset, updateDNA, deleteAsset, deleteAssets, addCampaign, updateCampaign, deleteCampaign, addNarrative, completeOnboarding, updateUserExtendedProfile, migrateLegacyImages, isLoading,
        addNotification, markNotificationRead, markAllNotificationsRead, clearNotifications,
        addScenario, addEnvironment, addAttire
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) throw new Error("useStore must be used within StoreProvider");
  return context;
};